// /portfolios/:portfolioID — portfolio dashboard (T065).
//
// Two focal panels:
//
//  1. Security Posture Heatmap
//     One coloured cell per project, arranged in a wrap-grid. Green = gate
//     passed, red = gate failed, grey = not yet scanned. Gives an at-a-glance
//     view of how many projects are passing or failing the quality gate.
//
//  2. Tech-Debt Breakdown
//     Horizontal proportion bar showing the split of Bugs / Vulnerabilities /
//     Code Smells across all projects in the portfolio, plus individual metric
//     cards with totals.
//
// Both panels derive from GET /api/v1/portfolios/{id}/summary which returns
// aggregated counts — no additional endpoints needed.

import { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Bug,
  Copy,
  Layers,
  Loader2,
  Radar,
  ShieldAlert,
  Wrench,
} from 'lucide-react';
import {
  ApiError,
  Application,
  Portfolio,
  PortfolioAggregate,
  fetcher,
} from '../lib/api';
import { FeatureGate } from '../components/FeatureLock';
import { PageHeader } from '../components/ui';

export function PortfolioDetail() {
  const { portfolioID } = useParams<{ portfolioID: string }>();
  const id = portfolioID ?? '';

  const portfolioKey = id ? `/api/v1/portfolios/${id}` : null;
  const summaryKey = id ? `/api/v1/portfolios/${id}/summary` : null;
  const applicationsKey = id ? `/api/v1/portfolios/${id}/applications` : null;

  const portfolio = useSWR<Portfolio>(portfolioKey, fetcher);
  const summary = useSWR<PortfolioAggregate>(summaryKey, fetcher);
  const applications = useSWR<Application[]>(applicationsKey, fetcher);

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <Link
        to="/portfolios"
        className="
          inline-flex items-center gap-1.5 mb-5 text-xs font-semibold
          uppercase tracking-[0.08em] text-fg-muted hover:text-fg
          transition-colors
        "
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        <span>All bundles</span>
      </Link>

      <FeatureGate feature="portfolio" label="Bundles">
        <PortfolioHeader
          isLoading={portfolio.isLoading}
          error={portfolio.error}
          portfolio={portfolio.data}
        />

        {/* Summary stat row */}
        {summary.data ? (
          <StatRow aggregate={summary.data} />
        ) : null}
        {!summary.data && summary.isLoading ? (
          <div className="mb-6 orb-card p-5 flex items-center gap-3 text-fg-muted">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden="true" />
            <span className="text-sm">Loading summary…</span>
          </div>
        ) : null}

        {/* Measures overview — SonarQube-style aggregate view (T209) */}
        {summary.data ? (
          <PortfolioMeasuresPanel aggregate={summary.data} />
        ) : null}

        {/* Main dashboard panels */}
        {summary.data ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <SecurityPosturePanel aggregate={summary.data} />
            <TechDebtPanel aggregate={summary.data} />
          </div>
        ) : null}

        {/* Applications list */}
        <ApplicationsPanel
          isLoading={applications.isLoading}
          error={applications.error}
          applications={applications.data}
        />
      </FeatureGate>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function PortfolioHeader({
  isLoading,
  error,
  portfolio,
}: {
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly portfolio?: Portfolio;
}) {
  if (isLoading) {
    return (
      <header className="mb-6">
        <p className="orb-eyebrow mb-1">orbital · bundle</p>
        <div className="h-9 w-64 bg-bg-inset rounded animate-pulse" />
      </header>
    );
  }
  if (error) {
    return (
      <PageHeader
        eyebrow="orbital · bundle"
        title="Bundle unavailable"
        description={<PortfolioErrorHint error={error} />}
      />
    );
  }
  if (!portfolio) return null;

  return (
    <PageHeader
      eyebrow="orbital · portfolio"
      icon={<Layers />}
      title={portfolio.name}
      description={
        <span className="flex items-center gap-3 text-xs">
          <span className="orb-code">{portfolio.key}</span>
          {portfolio.description ? (
            <span>{portfolio.description}</span>
          ) : null}
        </span>
      }
    />
  );
}

function PortfolioErrorHint({ error }: { readonly error: unknown }) {
  if (error instanceof ApiError && error.status === 404) {
    return (
      <span className="text-sm text-fg-muted">
        No such bundle (or it belongs to a different org).
      </span>
    );
  }
  let errorMsg: string;
  if (error instanceof ApiError) {
    errorMsg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    errorMsg = error.message;
  } else {
    errorMsg = 'request failed';
  }
  return (
    <span className="text-sm text-danger">
      {errorMsg}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Stat row
// ---------------------------------------------------------------------------

function StatRow({ aggregate: a }: { readonly aggregate: PortfolioAggregate }) {
  const gateTotal = a.gate_pass + a.gate_fail + a.gate_not_run;
  const passRate = gateTotal > 0 ? Math.round((a.gate_pass / gateTotal) * 100) : 0;

  return (
    <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
      <StatCard label="Projects" value={a.project_count} />
      <StatCard label="Applications" value={a.application_count} />
      <StatCard label="Total Issues" value={a.total_issues} danger={a.total_issues > 0} />
      <StatCard label="Gate Pass Rate" value={`${passRate}%`} danger={passRate < 50} />
    </div>
  );
}

function StatCard({
  label,
  value,
  danger,
}: {
  readonly label: string;
  readonly value: number | string;
  readonly danger?: boolean;
}) {
  return (
    <div className="orb-card p-4 flex flex-col gap-1">
      <span className="orb-eyebrow text-xs text-fg-subtle">{label}</span>
      <span
        className={[
          'font-display text-2xl font-semibold',
          danger ? 'text-danger' : 'text-fg',
        ].join(' ')}
      >
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Portfolio Measures panel (T209) — SonarQube-style aggregate overview
// ---------------------------------------------------------------------------

function PortfolioMeasuresPanel({ aggregate: a }: { readonly aggregate: PortfolioAggregate }) {
  const gateTotal = a.gate_pass + a.gate_fail + a.gate_not_run;
  const passRate = gateTotal > 0 ? (a.gate_pass / gateTotal) * 100 : 0;
  let passRateTone: string;
  if (passRate >= 80) {
    passRateTone = 'text-accent';
  } else if (passRate >= 50) {
    passRateTone = 'text-warning';
  } else {
    passRateTone = 'text-danger';
  }

  return (
    <section
      className="mb-6 orb-card p-5 space-y-4"
      aria-label="Bundle measures overview"
    >
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-accent shrink-0" aria-hidden="true" />
        <h2 className="font-display text-sm font-semibold text-fg">Measures</h2>
        <span className="ml-auto text-xs text-fg-subtle">
          {a.project_count} project{a.project_count === 1 ? '' : 's'} · aggregated
        </span>
      </div>

      {/* Issue-type columns — mirrors the SonarQube 3-column reliability/security/maintainability layout */}
      <div className="grid grid-cols-3 gap-3">
        <PortfolioMetricCol
          icon={<Bug className="h-4 w-4" />}
          label="Bugs"
          value={a.total_bugs}
          danger={a.total_bugs > 0}
          sub="Reliability"
        />
        <PortfolioMetricCol
          icon={<ShieldAlert className="h-4 w-4" />}
          label="Vulnerabilities"
          value={a.total_vulnerabilities}
          danger={a.total_vulnerabilities > 0}
          sub="Security"
        />
        <PortfolioMetricCol
          icon={<Wrench className="h-4 w-4" />}
          label="Code Smells"
          value={a.total_code_smells}
          sub="Maintainability"
        />
      </div>

      {/* Secondary metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div className="flex flex-col gap-1 p-3 rounded-md bg-bg-inset border border-border">
          <div className="flex items-center gap-1.5 text-fg-subtle text-xs">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>Total Issues</span>
          </div>
          <span className={`font-display text-lg font-semibold ${a.total_issues > 0 ? 'text-danger' : 'text-fg'}`}>
            {a.total_issues.toLocaleString()}
          </span>
        </div>
        <div className="flex flex-col gap-1 p-3 rounded-md bg-bg-inset border border-border">
          <div className="flex items-center gap-1.5 text-fg-subtle text-xs">
            <Radar className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>Gate Pass Rate</span>
          </div>
          <span className={`font-display text-lg font-semibold ${passRateTone}`}>
            {gateTotal > 0 ? `${Math.round(passRate)}%` : '—'}
          </span>
          {gateTotal > 0 ? (
            <span className="text-xs text-fg-muted">
              {a.gate_pass}/{gateTotal} projects
            </span>
          ) : null}
        </div>
        <div className="flex flex-col gap-1 p-3 rounded-md bg-bg-inset border border-border">
          <div className="flex items-center gap-1.5 text-fg-subtle text-xs">
            <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>Applications</span>
          </div>
          <span className="font-display text-lg font-semibold text-fg">
            {a.application_count.toLocaleString()}
          </span>
          <span className="text-xs text-fg-muted">in this bundle</span>
        </div>
      </div>

      <p className="text-xs text-fg-subtle italic">
        A–E reliability, security, and maintainability ratings are computed per-project —
        view each project&rsquo;s detail page for individual ratings and SQALE debt.
      </p>
    </section>
  );
}

function PortfolioMetricCol({
  icon,
  label,
  value,
  danger,
  sub,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: number;
  readonly danger?: boolean;
  readonly sub: string;
}) {
  let valueCls: string;
  if (danger && value > 0) {
    valueCls = 'text-danger';
  } else if (value === 0) {
    valueCls = 'text-accent';
  } else {
    valueCls = 'text-fg';
  }
  return (
    <div className="flex flex-col items-center gap-2 p-4 rounded-md bg-bg-inset border border-border text-center">
      <div className="flex items-center gap-1 text-xs text-fg-subtle">
        {icon}
        <span>{sub}</span>
      </div>
      <span className={`font-display text-2xl font-semibold ${valueCls}`}>
        {value.toLocaleString()}
      </span>
      <span className="text-xs text-fg-muted">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Security posture heatmap panel
// ---------------------------------------------------------------------------

function SecurityPosturePanel({ aggregate: a }: { readonly aggregate: PortfolioAggregate }) {
  const total = a.gate_pass + a.gate_fail + a.gate_not_run;

  type CellState = 'pass' | 'fail' | 'not_run';
  const cells: CellState[] = [
    ...Array<CellState>(a.gate_pass).fill('pass'),
    ...Array<CellState>(a.gate_fail).fill('fail'),
    ...Array<CellState>(a.gate_not_run).fill('not_run'),
  ];

  const cellColor: Record<CellState, string> = {
    pass: 'bg-accent',
    fail: 'bg-danger',
    not_run: 'bg-bg-inset border border-border',
  };
  const cellLabel: Record<CellState, string> = {
    pass: 'Gate passed',
    fail: 'Gate failed',
    not_run: 'Not scanned',
  };

  return (
    <section
      className="orb-card p-5 flex flex-col gap-4"
      aria-label="Security posture heatmap"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display text-base font-semibold text-fg flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-accent shrink-0" aria-hidden="true" />
          Security Posture
        </h2>
        <span className="text-xs text-fg-subtle">{total} project{total === 1 ? '' : 's'}</span>
      </div>

      {total === 0 ? (
        <p className="text-sm text-fg-muted">
          No projects have been scanned yet. Run{' '}
          <span className="orb-code">orbital-lint scanner run</span> to populate
          quality gate data.
        </p>
      ) : (
        <>
          {/* Heatmap grid — each cell = one project */}
          <div
            className="flex flex-wrap gap-1.5"
            role="img"
            aria-label={`${a.gate_pass} passing, ${a.gate_fail} failing, ${a.gate_not_run} not run`}
          >
            {cells.map((state, cellIdx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${state}-${cellIdx}`} className={`w-5 h-5 rounded-sm ${cellColor[state]}`} title={cellLabel[state]} />
            ))}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-xs text-fg-muted">
            <LegendItem color="bg-accent" label={`Pass (${a.gate_pass})`} />
            <LegendItem color="bg-danger" label={`Fail (${a.gate_fail})`} />
            <LegendItem
              color="bg-bg-inset border border-border"
              label={`Not run (${a.gate_not_run})`}
            />
          </div>

          {/* Gate pass rate bar */}
          {total > 0 ? (
            <div>
              <div className="mb-1 flex justify-between text-xs text-fg-subtle">
                <span>Gate pass rate</span>
                <span>{Math.round((a.gate_pass / total) * 100)}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden bg-bg-inset">
                <div
                  className="h-full bg-accent rounded-full transition-all"
                  style={{ width: `${(a.gate_pass / total) * 100}%` }}
                  aria-hidden="true"
                />
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function LegendItem({ color, label }: { readonly color: string; readonly label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-3 h-3 rounded-sm shrink-0 ${color}`} aria-hidden="true" />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tech-debt breakdown panel
// ---------------------------------------------------------------------------

function TechDebtPanel({ aggregate: a }: { readonly aggregate: PortfolioAggregate }) {
  const tracked = a.total_bugs + a.total_vulnerabilities + a.total_code_smells;

  // Proportion widths for the stacked bar (avoid division by zero).
  const bugPct = tracked > 0 ? (a.total_bugs / tracked) * 100 : 0;
  const vulnPct = tracked > 0 ? (a.total_vulnerabilities / tracked) * 100 : 0;
  const smellPct = tracked > 0 ? (a.total_code_smells / tracked) * 100 : 0;

  return (
    <section
      className="orb-card p-5 flex flex-col gap-4"
      aria-label="Tech-debt breakdown"
    >
      <h2 className="font-display text-base font-semibold text-fg flex items-center gap-2">
        <Bug className="h-4 w-4 text-accent shrink-0" aria-hidden="true" />
        Tech-Debt Overview
      </h2>

      {tracked === 0 ? (
        <p className="text-sm text-fg-muted">
          No issues found across bundle projects — quality gate is green.
        </p>
      ) : (
        <>
          {/* Stacked proportion bar */}
          <div>
            <div className="mb-1 text-xs text-fg-subtle">Issue mix</div>
            <div
              className="h-3 rounded-full overflow-hidden flex bg-bg-inset"
              role="img"
              aria-label={`Bugs ${Math.round(bugPct)}%, Vulnerabilities ${Math.round(vulnPct)}%, Code Smells ${Math.round(smellPct)}%`}
            >
              {bugPct > 0 ? (
                <div
                  className="h-full bg-danger"
                  style={{ width: `${bugPct}%` }}
                  title={`Bugs: ${Math.round(bugPct)}%`}
                />
              ) : null}
              {vulnPct > 0 ? (
                <div
                  className="h-full bg-warning"
                  style={{ width: `${vulnPct}%` }}
                  title={`Vulnerabilities: ${Math.round(vulnPct)}%`}
                />
              ) : null}
              {smellPct > 0 ? (
                <div
                  className="h-full bg-fg-subtle"
                  style={{ width: `${smellPct}%` }}
                  title={`Code Smells: ${Math.round(smellPct)}%`}
                />
              ) : null}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-fg-muted">
              <LegendItem color="bg-danger" label={`Bugs (${Math.round(bugPct)}%)`} />
              <LegendItem color="bg-warning" label={`Vulns (${Math.round(vulnPct)}%)`} />
              <LegendItem color="bg-fg-subtle" label={`Smells (${Math.round(smellPct)}%)`} />
            </div>
          </div>

          {/* Metric cards */}
          <div className="grid grid-cols-2 gap-2">
            <IssueMetricCard
              icon={<Bug className="h-3.5 w-3.5" />}
              label="Bugs"
              value={a.total_bugs}
              danger
            />
            <IssueMetricCard
              icon={<ShieldAlert className="h-3.5 w-3.5" />}
              label="Vulnerabilities"
              value={a.total_vulnerabilities}
              danger
            />
            <IssueMetricCard
              icon={<Wrench className="h-3.5 w-3.5" />}
              label="Code Smells"
              value={a.total_code_smells}
            />
            <IssueMetricCard
              icon={<AlertTriangle className="h-3.5 w-3.5" />}
              label="All Issues"
              value={a.total_issues}
              danger={a.total_issues > 0}
            />
          </div>
        </>
      )}
    </section>
  );
}

function IssueMetricCard({
  icon,
  label,
  value,
  danger,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: number;
  readonly danger?: boolean;
}) {
  let valueTone: string;
  if (danger && value > 0) {
    valueTone = 'text-danger';
  } else if (value === 0) {
    valueTone = 'text-fg-muted';
  } else {
    valueTone = 'text-fg';
  }
  return (
    <div className="flex flex-col gap-1 p-3 rounded-md bg-bg-inset border border-border">
      <div className="flex items-center gap-1.5 text-fg-subtle text-xs">
        <span className="h-3.5 w-3.5 shrink-0" aria-hidden="true">
          {icon}
        </span>
        <span className="truncate">{label}</span>
      </div>
      <span className={`font-display text-xl font-semibold ${valueTone}`}>
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Applications list
// ---------------------------------------------------------------------------

function ApplicationsPanel({
  isLoading,
  error,
  applications,
}: {
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly applications?: Application[];
}) {
  return (
    <section aria-label="Applications in bundle">
      <h2 className="font-display text-base font-semibold text-fg mb-3 flex items-center gap-2">
        <Layers className="h-4 w-4 text-accent shrink-0" aria-hidden="true" />
        Applications
      </h2>
      {isLoading ? (
        <div className="orb-card p-5 flex items-center gap-3 text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden="true" />
          <span className="text-sm">Loading applications…</span>
        </div>
      ) : null}
      {!isLoading && error ? (
        <InlineError error={error} />
      ) : null}
      {!isLoading && !error && (!applications || applications.length === 0) ? (
        <div className="orb-card p-6 text-center text-sm text-fg-muted">
          No applications assigned to this bundle yet.
        </div>
      ) : null}
      {!isLoading && !error && applications && applications.length > 0 ? (
        <ul className="orb-card divide-y divide-border">
          {applications.map((app) => (
            <li
              key={app.id}
              className="flex items-center gap-4 px-5 py-3 text-sm"
            >
              <Layers className="h-4 w-4 shrink-0 text-fg-subtle" aria-hidden="true" />
              <span className="font-display text-sm text-fg truncate">
                {app.name}
              </span>
              <span className="orb-code text-xs text-fg-subtle">{app.key}</span>
              {app.description ? (
                <span className="text-xs text-fg-muted truncate max-w-xs hidden sm:block">
                  {app.description}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function InlineError({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'Unknown error';
  }
  return (
    <div role="alert" className="orb-card p-4 border-danger/40 bg-danger/5">
      <p className="text-sm text-danger">{msg}</p>
    </div>
  );
}
